import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { toast } from "@/components/Toast";
import { useAuth } from "@/contexts/AuthContext";
import {
  ArrowLeft, ClipboardCheck, Plus, Trash2, Check, AlertTriangle, Camera,
  CalendarClock, ChevronDown, ChevronUp, Settings2, Save, Video,
} from "lucide-react";
import { AttachmentPicker, AttachmentGrid } from "@/components/AttachmentPicker";
import { fileSrc } from "@/lib/uploads";
import { isOperationalRole } from "@/lib/roles";
import { ClientToolsLog } from "@/components/ClientToolsLog"; // Fix AR

const STATUS_META = {
  verificado: { label: "Verificado", icon: Check, color: "#16a34a" },
  faltante: { label: "Faltante / No funcional", icon: AlertTriangle, color: "#BF3437" },
};

// Fix AB (2026-09-17): misma etiqueta/color que ya se usa en la ficha del
// cliente (solapa "Elementos de trabajo") para el dueño de cada elemento.
const MATERIAL_OWNER_META = {
  invict: { label: "INVICT", badge: "red" },
  cliente: { label: "CLIENTE", badge: "blue" },
  sin_especificar: { label: "SIN ESPECIFICAR", badge: "gray" },
};

function arToday() {
  // Aproximación en cliente sólo para el valor inicial del selector de fecha
  // (hora Argentina = UTC-3); el backend es la fuente de verdad real.
  const d = new Date(Date.now() - 3 * 60 * 60 * 1000);
  return d.toISOString().slice(0, 10);
}

export default function WorkItems() {
  const { user } = useAuth();
  const role = user?.role;
  const canManage = role === "admin" || role === "supervisor";
  const canCheck = isOperationalRole(role); // checklist propio (kit personal)
  const [tab, setTab] = useState("checklist");

  const [items, setItems] = useState([]);
  const [catalogItems, setCatalogItems] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [closure, setClosure] = useState(null);
  const [closureBuf, setClosureBuf] = useState([]);
  const [closureBusy, setClosureBusy] = useState(false);
  const [newItemName, setNewItemName] = useState("");
  const [adding, setAdding] = useState(false);
  const [busy, setBusy] = useState({});
  const [photoFor, setPhotoFor] = useState(null);   // item_id whose photo modal is open
  const [photoBuf, setPhotoBuf] = useState([]);     // pending photo before submit
  const [faltanteFor, setFaltanteFor] = useState(null); // item_id whose "Falta" note panel is open
  const [faltanteNote, setFaltanteNote] = useState("");
  // Fix W (2026-09-11): checklist por objetivo -- "" = elementos globales.
  const [itemsClientId, setItemsClientId] = useState("");
  const [manageClients, setManageClients] = useState([]);

  useEffect(() => {
    if (!canManage) return;
    (async () => {
      try { const { data } = await api.get("/clients"); setManageClients(data); }
      catch (e) { toast.error(formatApiError(e)); }
    })();
  }, [canManage]);

  const refresh = async () => {
    try {
      if (canManage && itemsClientId) {
        // Fix AB (2026-09-17): con un objetivo seleccionado, esto es el
        // catálogo de ese cliente en modo lectura (se edita en la ficha del
        // cliente) -- `items` se usa solo para calcular el total que va a
        // ver el vigilador (globales + catálogo, deduplicado).
        const [mat, gl] = await Promise.all([
          api.get(`/clients/${itemsClientId}/materials`),
          api.get("/work-items"),
        ]);
        setCatalogItems(mat.data);
        setItems(gl.data);
        return;
      }
      const [it, st, cl] = await Promise.all([
        api.get("/work-items"),
        api.get("/work-items/status"),
        canCheck ? api.get("/work-items/checklist-closure") : Promise.resolve({ data: null }),
      ]);
      setItems(it.data);
      setStatuses(st.data);
      setClosure(cl.data);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  useEffect(() => { refresh(); }, [itemsClientId]);

  // Latest status per item para el usuario actual, SOLO de hoy (el backend
  // ya filtra por fecha=hoy en /work-items/status sin parámetro).
  const myStatusByItem = useMemo(() => {
    const m = {};
    for (const s of statuses) {
      if (s.user_id !== user?.id) continue;
      m[s.item_id] = s;
    }
    return m;
  }, [statuses, user]);

  const myCompletedToday = Object.keys(myStatusByItem).length;
  const myMissingToday = Object.values(myStatusByItem).filter((s) => s.status === "faltante").length;
  const myChecklistComplete = items.length > 0 && myCompletedToday >= items.length;

  // Fix AB (2026-09-17): total que va a ver el vigilador de ese objetivo =
  // globales + catálogo, sin contar dos veces un mismo nombre.
  const normName = (s) => (s || "").trim().toLowerCase().replace(/\s+/g, " ");
  const unionCount = useMemo(() => {
    const names = new Set(items.map((it) => normName(it.name)));
    let n = names.size;
    for (const m of catalogItems) {
      const key = normName(m.name);
      if (!names.has(key)) { names.add(key); n++; }
    }
    return n;
  }, [items, catalogItems]);

  const addItem = async () => {
    const name = newItemName.trim();
    if (!name) return;
    setAdding(true);
    try {
      await api.post("/work-items", { name });
      setNewItemName("");
      toast.success("Elemento agregado");
      await refresh();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setAdding(false); }
  };

  const removeItem = async (id) => {
    if (!window.confirm("¿Quitar este elemento del checklist?")) return;
    try {
      await api.delete(`/work-items/${id}`);
      toast.success("Elemento removido");
      await refresh();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const setStatus = async (item_id, status, attachments = [], notes = null) => {
    setBusy((b) => ({ ...b, [item_id]: true }));
    try {
      await api.post("/work-items/status", { item_id, status, attachments, notes });
      await refresh();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy((b) => ({ ...b, [item_id]: false })); }
  };

  const submitWithPhoto = async (item_id) => {
    if (!photoBuf.length) { toast.error("Adjuntá al menos una foto o video"); return; }
    await setStatus(item_id, "verificado", photoBuf);
    setPhotoFor(null); setPhotoBuf([]);
  };

  const submitFaltante = async (item_id) => {
    await setStatus(item_id, "faltante", [], faltanteNote.trim() || null);
    setFaltanteFor(null); setFaltanteNote("");
  };

  const submitClosure = async () => {
    // Fix BO (2026-11-03): la foto/video general es opcional.
    setClosureBusy(true);
    try {
      await api.post("/work-items/checklist-closure", { attachments: closureBuf });
      toast.success("Checklist cerrado");
      setClosureBuf([]);
      await refresh();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setClosureBusy(false); }
  };

  const backTo = role === "admin" ? "/admin" : role === "supervisor" ? "/supervisor" : "/vigilador";

  return (
    <div style={{ padding: "8px 16px 32px", maxWidth: 980, margin: "0 auto" }} data-testid="work-items-page">
      <Link to={backTo} style={{ display: "inline-flex", alignItems: "center", gap: 6, color: "var(--invict-text-muted)", fontSize: 12, marginBottom: 12, textDecoration: "none", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em" }}>
        <ArrowLeft size={14} /> Volver
      </Link>

      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
        <ClipboardCheck size={26} style={{ color: "#BF3437" }} />
        <h1 style={{ fontSize: 26, fontWeight: 900, margin: 0, color: "#0A0A0A", letterSpacing: "-0.02em" }}>Elementos de Trabajo</h1>
      </div>
      <div style={{ fontSize: 12, color: "var(--invict-text-muted)", marginBottom: 16 }}>
        Checklist del equipo personal de cada vigilador (capa poncho, libro de actas, linterna...).
        {canManage && " · Admin y Supervisor pueden agregar o quitar elementos."}
      </div>

      {/* Fix AB (2026-09-17): el checklist es SIEMPRE la suma del kit
          personal (globales) + el catálogo del objetivo de hoy (si tiene). */}
      {canCheck && items.length > 0 && (
        <div style={{ fontSize: 11, color: "var(--invict-text-muted)", marginBottom: 12, display: "flex", alignItems: "center", gap: 6 }} data-testid="work-items-source-hint">
          <ClipboardCheck size={12} />
          {(() => {
            const objetivo = items.find((it) => it.client_id)?.client_name;
            return objetivo
              ? <>Tu kit personal + el catálogo de <b style={{ color: "#0A0A0A" }}>{objetivo}</b>.</>
              : <>Tu kit personal (sin catálogo cargado para hoy en tu objetivo).</>;
          })()}
        </div>
      )}

      {canCheck && items.length > 0 && (
        <div
          className="card"
          data-testid="work-items-today-banner"
          style={{
            padding: "14px 16px", marginBottom: 18, display: "flex", alignItems: "center", gap: 12,
            borderLeft: `4px solid ${myChecklistComplete ? "#16a34a" : "#BF3437"}`,
          }}
        >
          {myChecklistComplete ? <Check size={20} style={{ color: "#16a34a", flexShrink: 0 }} /> : <AlertTriangle size={20} style={{ color: "#BF3437", flexShrink: 0 }} />}
          <div>
            <div style={{ fontWeight: 800, fontSize: 14, color: "#0A0A0A" }}>
              {myChecklistComplete ? "Checklist de HOY: completo" : `Checklist de HOY: te faltan ${items.length - myCompletedToday} de ${items.length} ítems`}
            </div>
            {myMissingToday > 0 && (
              <div style={{ fontSize: 12, color: "#BF3437", fontWeight: 700, marginTop: 2 }}>
                {myMissingToday} elemento{myMissingToday > 1 ? "s" : ""} marcado{myMissingToday > 1 ? "s" : ""} como faltante — ya se avisó a supervisión.
              </div>
            )}
          </div>
        </div>
      )}

      {canCheck && items.length > 0 && myChecklistComplete && (
        <div className="card" data-testid="work-items-closure-panel" style={{ padding: "14px 16px", marginBottom: 18, borderLeft: `4px solid ${closure ? "#16a34a" : "#0A0A0A"}` }}>
          {closure ? (
            <div>
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
                <Check size={16} style={{ color: "#16a34a" }} />
                <div>
                  <div style={{ fontWeight: 800, fontSize: 13, color: "#16a34a" }}>{(closure.attachments?.length || closure.photo_url) ? "Checklist cerrado con evidencia" : "Checklist cerrado"}</div>
                  <div style={{ fontSize: 11, color: "var(--invict-text-muted)" }}>Ya quedó guardada junto al registro de hoy.</div>
                </div>
              </div>
              <AttachmentGrid items={closure.attachments?.length ? closure.attachments : (closure.photo_url ? [{ url: closure.photo_url, kind: "image" }] : [])} />
            </div>
          ) : (
            <>
              <div style={{ fontWeight: 800, fontSize: 13, color: "#0A0A0A", marginBottom: 6 }}>Cerrar checklist</div>
              <div style={{ fontSize: 11, color: "var(--invict-text-muted)", marginBottom: 8 }}>
                Completaste todos los ítems. Si querés, adjuntá hasta 5 fotos y/o un video general de los elementos entregados / estado del equipo (opcional).
              </div>
              <AttachmentPicker value={closureBuf} onChange={setClosureBuf} accept="image_video" max={5} label="Foto o video general (opcional)" />
              <button onClick={submitClosure} disabled={closureBusy} className="btn-primary" style={{ marginTop: 8, padding: "8px 14px" }} data-testid="work-items-closure-submit">
                {closureBusy ? "Guardando..." : "Cerrar checklist"}
              </button>
            </>
          )}
        </div>
      )}

      {canManage && (
        <div style={{ display: "flex", gap: 8, marginBottom: 18 }}>
          <TabBtn active={tab === "checklist"} onClick={() => setTab("checklist")} label="Elementos del checklist" testid="work-items-tab-checklist" />
          <TabBtn active={tab === "cumplimiento"} onClick={() => setTab("cumplimiento")} label="Cumplimiento diario" testid="work-items-tab-cumplimiento" />
          <TabBtn active={tab === "herramientas"} onClick={() => setTab("herramientas")} label="Herramientas del cliente" testid="work-items-tab-herramientas" />
        </div>
      )}

      {(!canManage || tab === "checklist") && (
      <>
      {canManage && (
        <div className="card" style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14, flexWrap: "wrap" }} data-testid="work-items-client-selector-card">
          <div style={{ fontSize: 12, fontWeight: 800, color: "#0A0A0A" }}>Editando</div>
          <div className="field" style={{ margin: 0 }}>
            <select value={itemsClientId} onChange={(e) => setItemsClientId(e.target.value)} data-testid="work-items-client-select">
              <option value="">Elementos globales (kit personal, todos los objetivos)</option>
              {manageClients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
        </div>
      )}
      {canManage && itemsClientId && (
        <div className="card" style={{ padding: "12px 16px", marginBottom: 14, borderLeft: "4px solid #0A0A0A", fontSize: 12, color: "#0A0A0A" }} data-testid="work-items-catalog-hint">
          Estos elementos salen de la ficha del objetivo (solapa "Elementos de trabajo"), no se cargan acá. El vigilador de este objetivo ve estos {catalogItems.length} elemento{catalogItems.length === 1 ? "" : "s"} del catálogo más los {items.length} global{items.length === 1 ? "" : "es"} (sin repetir nombres): <b>{unionCount} en total</b>.
        </div>
      )}
      {canManage && itemsClientId ? (
        <div className="card" style={{ padding: 16, marginBottom: 18 }} data-testid="work-items-catalog-readonly">
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10, flexWrap: "wrap", gap: 8 }}>
            <div style={{ fontSize: 11, fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.1em", color: "#0A0A0A" }}>
              Catálogo de {manageClients.find((c) => c.id === itemsClientId)?.name || "este objetivo"} ({catalogItems.length})
            </div>
            <Link to={`/${role}/clientes/${itemsClientId}?tab=materials`} className="btn-ghost" style={{ padding: "6px 12px", fontSize: 11 }} data-testid="work-items-catalog-edit-link">
              Editar en la ficha del cliente
            </Link>
          </div>
          {catalogItems.length === 0 ? (
            <div style={{ padding: 20, textAlign: "center", color: "var(--invict-text-muted)", fontSize: 13 }}>
              Este objetivo todavía no tiene elementos cargados en su ficha. Sus vigiladores ven solo los {items.length} global{items.length === 1 ? "" : "es"}.
            </div>
          ) : catalogItems.map((it) => {
            const ownerMeta = MATERIAL_OWNER_META[it.owner || "sin_especificar"] || MATERIAL_OWNER_META.sin_especificar;
            return (
              <div key={it.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 0", borderBottom: "1px solid var(--invict-bone-3)" }} data-testid={`work-items-catalog-row-${it.id}`}>
                <span style={{ fontSize: 14, fontWeight: 700, color: "#0A0A0A" }}>{it.name}</span>
                <span className={`badge ${ownerMeta.badge}`} data-testid={`work-items-catalog-owner-${it.id}`}>{ownerMeta.label}</span>
              </div>
            );
          })}
        </div>
      ) : (
      <>
      {/* Master list of items */}
      <div className="card" style={{ padding: 16, marginBottom: 18 }} data-testid="work-items-master">
        <div style={{ fontSize: 11, fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.1em", color: "#0A0A0A", marginBottom: 10 }}>
          Elementos del checklist ({items.length})
        </div>

        {items.length === 0 && (
          <div style={{ padding: 20, textAlign: "center", color: "var(--invict-text-muted)", fontSize: 13 }}>
            Todavía no hay elementos cargados.
            {canManage ? " Agregá el primero abajo." : " Pedile a un supervisor o administrador que los cargue."}
          </div>
        )}


        {items.map((it) => {
          const mine = myStatusByItem[it.id];
          const Ic = mine ? STATUS_META[mine.status].icon : null;
          const photoOpen = photoFor === it.id;
          return (
            <div key={it.id} style={{ borderBottom: "1px solid var(--invict-bone-3)" }} data-testid={`work-item-row-${it.id}`}>
              <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 0" }}>
                <div style={{ flex: 1, fontSize: 14, fontWeight: 700, color: "#0A0A0A" }}>
                  {it.name}
                  {it.owner && (
                    <span className={`badge ${MATERIAL_OWNER_META[it.owner]?.badge || "gray"}`} style={{ marginLeft: 8, fontSize: 9 }} data-testid={`work-item-owner-${it.id}`}>
                      {MATERIAL_OWNER_META[it.owner]?.label || "SIN ESPECIFICAR"}
                    </span>
                  )}
                  {mine && Ic && (
                    <span style={{ marginLeft: 10, display: "inline-flex", alignItems: "center", gap: 4, fontSize: 11, fontWeight: 700, color: STATUS_META[mine.status].color }}>
                      <Ic size={12} /> {STATUS_META[mine.status].label}
                    </span>
                  )}
                  {mine?.attachments?.length > 0 && (
                    <span style={{ marginLeft: 8, display: "inline-flex", alignItems: "center", gap: 4, verticalAlign: "middle" }}>
                      <a href={fileSrc(mine.attachments[0].url)} target="_blank" rel="noreferrer" data-testid={`work-item-photo-${it.id}`}>
                        {mine.attachments[0].kind === "video" ? (
                          <div style={{ width: 28, height: 28, background: "#000", borderRadius: 3, display: "flex", alignItems: "center", justifyContent: "center" }}>
                            <Video size={13} color="#fff" />
                          </div>
                        ) : (
                          <img src={fileSrc(mine.attachments[0].url)} alt="" style={{ width: 28, height: 28, objectFit: "cover", borderRadius: 3, border: "1px solid var(--invict-bone-3)" }}/>
                        )}
                      </a>
                      {mine.attachments.length > 1 && <span style={{ fontSize: 10, fontWeight: 800, color: "var(--invict-text-muted)" }}>+{mine.attachments.length - 1}</span>}
                    </span>
                  )}
                </div>

                {canCheck && (
                  <div style={{ display: "flex", gap: 6 }}>
                    {["verificado", "faltante"].map((v) => {
                      const M = STATUS_META[v];
                      const active = mine?.status === v;
                      const MIc = M.icon;
                      return (
                        <button key={v}
                          onClick={() => {
                            if (v === "faltante") {
                              setPhotoFor(null); setPhotoBuf([]);
                              setFaltanteFor(faltanteFor === it.id ? null : it.id);
                              setFaltanteNote(mine?.status === "faltante" ? (mine.notes || "") : "");
                            } else {
                              setFaltanteFor(null);
                              setStatus(it.id, v);
                            }
                          }}
                          disabled={!!busy[it.id]}
                          style={{
                            padding: "6px 10px",
                            border: `2px solid ${active ? M.color : "var(--invict-bone-3)"}`,
                            background: active ? M.color : "white",
                            color: active ? "white" : "var(--invict-text-muted)",
                            fontWeight: 700, fontSize: 11, borderRadius: 2, cursor: busy[it.id] ? "wait" : "pointer",
                            display: "flex", alignItems: "center", gap: 4,
                            textTransform: "uppercase", letterSpacing: "0.05em",
                          }}
                          data-testid={`work-item-${it.id}-${v}`}
                        >
                          <MIc size={11} /> {v === "verificado" ? "OK" : "Falta"}
                        </button>
                      );
                    })}
                    <button onClick={() => { setFaltanteFor(null); setPhotoFor(photoOpen ? null : it.id); setPhotoBuf([]); }}
                      title="OK con foto"
                      style={{
                        padding: "6px 10px",
                        border: `2px solid ${photoOpen ? "#0A0A0A" : "var(--invict-bone-3)"}`,
                        background: photoOpen ? "#0A0A0A" : "white",
                        color: photoOpen ? "white" : "var(--invict-text-muted)",
                        fontWeight: 700, fontSize: 11, borderRadius: 2, cursor: "pointer",
                        display: "flex", alignItems: "center", gap: 4,
                      }}
                      data-testid={`work-item-photo-toggle-${it.id}`}
                    >
                      <Camera size={11}/>
                    </button>
                  </div>
                )}

                {canManage && (
                  <button onClick={() => removeItem(it.id)} title="Quitar"
                    style={{ marginLeft: 8, padding: 6, border: "1px solid var(--invict-bone-3)", background: "white", color: "#BF3437", borderRadius: 2, cursor: "pointer" }}
                    data-testid={`work-item-remove-${it.id}`}
                  >
                    <Trash2 size={14} />
                  </button>
                )}
              </div>

              {canCheck && photoOpen && (
                <div style={{ padding: "10px 0 14px", background: "var(--invict-bone-2)", margin: "0 -8px", paddingLeft: 12, paddingRight: 12, borderRadius: 2 }} data-testid={`work-item-photo-panel-${it.id}`}>
                  <div style={{ fontSize: 11, color: "var(--invict-text-muted)", marginBottom: 6 }}>
                    Adjuntá hasta 5 fotos y/o un video del elemento entregado (capa poncho, linterna, etc.) y se va a guardar como evidencia OK.
                  </div>
                  <AttachmentPicker value={photoBuf} onChange={setPhotoBuf} accept="image_video" max={5} label="Foto o video del elemento"/>
                  <button onClick={() => submitWithPhoto(it.id)} disabled={!photoBuf.length || !!busy[it.id]}
                    className="btn-primary"
                    style={{ marginTop: 8, padding: "8px 14px" }}
                    data-testid={`work-item-photo-submit-${it.id}`}
                  >
                    Confirmar OK con evidencia
                  </button>
                </div>
              )}

              {canCheck && faltanteFor === it.id && (
                <div style={{ padding: "10px 0 14px", background: "var(--invict-bone-2)", margin: "0 -8px", paddingLeft: 12, paddingRight: 12, borderRadius: 2 }} data-testid={`work-item-faltante-panel-${it.id}`}>
                  <div style={{ fontSize: 11, color: "var(--invict-text-muted)", marginBottom: 6 }}>
                    Contale a supervisión qué pasó con este elemento (opcional, pero ayuda a resolverlo más rápido).
                  </div>
                  <textarea
                    value={faltanteNote}
                    onChange={(e) => setFaltanteNote(e.target.value)}
                    placeholder="Ej: se rompió la linterna, hace falta reponerla"
                    data-testid={`work-item-faltante-notes-${it.id}`}
                    style={{ width: "100%", minHeight: 60, padding: "8px 10px", border: "2px solid var(--invict-bone-3)", background: "white", fontSize: 13, borderRadius: 2, resize: "vertical" }}
                  />
                  <button onClick={() => submitFaltante(it.id)} disabled={!!busy[it.id]}
                    style={{ marginTop: 8, padding: "8px 14px", border: `2px solid ${STATUS_META.faltante.color}`, background: STATUS_META.faltante.color, color: "white", fontWeight: 700, fontSize: 12, borderRadius: 2, cursor: "pointer" }}
                    data-testid={`work-item-faltante-submit-${it.id}`}
                  >
                    Confirmar Faltante
                  </button>
                </div>
              )}
            </div>
          );
        })}

        {canManage && (
          <div style={{ display: "flex", gap: 8, marginTop: 14 }}>
            <input
              value={newItemName}
              onChange={(e) => setNewItemName(e.target.value)}
              placeholder="Nuevo elemento (ej. Linterna)"
              onKeyDown={(e) => { if (e.key === "Enter") addItem(); }}
              data-testid="work-items-new-name"
              style={{ flex: 1, padding: "10px 12px", border: "2px solid var(--invict-bone-3)", background: "white", fontSize: 13, borderRadius: 2 }}
            />
            <button onClick={addItem} disabled={adding || !newItemName.trim()} className="btn-primary"
              style={{ padding: "10px 14px", display: "flex", alignItems: "center", gap: 6 }}
              data-testid="work-items-add">
              <Plus size={14} /> Agregar
            </button>
          </div>
        )}
      </div>
      </>
      )}
      </>
      )}

      {canManage && tab === "cumplimiento" && <ComplianceModule role={role} />}
      {/* Fix AR (2026-09-25): herramientas del cliente que ingresan y se retiran. */}
      {((canManage && tab === "herramientas") || (!canManage && canCheck)) && <ClientToolsLog role={role} />}
    </div>
  );
}

function TabBtn({ active, onClick, label, testid }) {
  return (
    <button type="button" onClick={onClick} data-testid={testid}
      style={{ display: "inline-flex", alignItems: "center", gap: 8, padding: "11px 18px", borderRadius: 8,
        border: "none", background: active ? "#0A0A0A" : "#fff", color: active ? "#FFFBF4" : "#0A0A0A",
        fontWeight: 800, fontSize: 13, cursor: "pointer", boxShadow: "0 1px 3px rgba(0,0,0,0.08)" }}>
      {label}
    </button>
  );
}

/* ============================ CUMPLIMIENTO DIARIO ============================ */
function ComplianceModule({ role }) {
  const isAdmin = role === "admin";
  const [date, setDate] = useState(arToday());
  const [clientId, setClientId] = useState("");
  const [clients, setClients] = useState([]);
  const [data, setData] = useState(null);
  const [settings, setSettings] = useState(null);
  const [savingSettings, setSavingSettings] = useState(false);
  const [expanded, setExpanded] = useState(null);

  useEffect(() => { (async () => {
    try { const c = await api.get("/clients"); setClients(c.data); } catch (e) { toast.error(formatApiError(e)); }
  })(); }, []);

  const loadSettings = async () => {
    try { const { data: s } = await api.get("/settings/work-checklist"); setSettings(s); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { loadSettings(); }, []);

  const load = async () => {
    try {
      const params = { date };
      if (clientId) params.client_id = clientId;
      const { data: d } = await api.get("/work-items/compliance", { params });
      setData(d);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, [date, clientId]);

  const saveSettings = async () => {
    setSavingSettings(true);
    try {
      const { data: s } = await api.patch("/settings/work-checklist", settings);
      setSettings(s);
      toast.success("Configuración guardada");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSavingSettings(false); }
  };

  const incompleteCount = data ? data.users.filter((u) => u.alert_incomplete).length : 0;
  const missingItemsCount = data ? data.users.filter((u) => u.missing_items.length > 0).length : 0;

  return (
    <div data-testid="work-items-compliance-module">
      <div className="card" style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14, flexWrap: "wrap" }}>
        <Settings2 size={16} style={{ color: "var(--invict-text-muted)" }} />
        <div style={{ fontSize: 12, fontWeight: 800, color: "#0A0A0A" }}>Horario límite</div>
        <div className="field" style={{ margin: 0 }}>
          <label>Tolerancia desde inicio de turno (min)</label>
          <input type="number" min={0} disabled={!isAdmin} value={settings?.grace_minutes ?? ""}
            onChange={(e) => setSettings((s) => ({ ...s, grace_minutes: Number(e.target.value) }))}
            data-testid="work-checklist-grace-minutes" style={{ width: 90 }} />
        </div>
        <div className="field" style={{ margin: 0 }}>
          <label>Respaldo sin turno cargado (hora AR)</label>
          <input type="time" disabled={!isAdmin} value={settings?.fallback_deadline_time ?? ""}
            onChange={(e) => setSettings((s) => ({ ...s, fallback_deadline_time: e.target.value }))}
            data-testid="work-checklist-fallback-time" style={{ width: 110 }} />
        </div>
        {isAdmin && (
          <button className="btn-primary" onClick={saveSettings} disabled={savingSettings} data-testid="work-checklist-settings-save"
            style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "8px 14px" }}>
            <Save size={13} /> {savingSettings ? "Guardando..." : "Guardar"}
          </button>
        )}
      </div>

      <div className="card" style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 14 }}>
        <div className="field" style={{ margin: 0 }}>
          <label>Fecha</label>
          <input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="work-checklist-filter-date" />
        </div>
        <div className="field" style={{ margin: 0 }}>
          <label>Objetivo</label>
          <select value={clientId} onChange={(e) => setClientId(e.target.value)} data-testid="work-checklist-filter-client">
            <option value="">Todos</option>
            {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </div>
        {date !== arToday() && (
          <button className="btn-ghost" onClick={() => setDate(arToday())} style={{ alignSelf: "flex-end" }} data-testid="work-checklist-filter-today-btn">Volver a hoy</button>
        )}
      </div>

      {data && (
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 16 }} data-testid="work-checklist-counters">
          <Counter label="Sin completar (fuera de plazo)" value={incompleteCount} color="#BF3437" testid="work-checklist-counter-incomplete" />
          <Counter label="Con elemento faltante" value={missingItemsCount} color="#d97706" testid="work-checklist-counter-missing" />
          <Counter label="Sin turno cargado en Cronograma" value={data.sin_turno_cargado_count} color="#6B655A" testid="work-checklist-counter-noshift" />
        </div>
      )}

      {data == null ? (
        <div className="skel" style={{ height: 100 }} />
      ) : data.users.length === 0 ? (
        <div className="card" style={{ textAlign: "center", padding: 30, color: "var(--invict-text-muted)" }} data-testid="work-checklist-empty">
          Nadie tenía turno o ronda asignada este día{clientId ? " en este objetivo" : ""}.
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }} data-testid="work-checklist-user-list">
          {data.users.map((u) => {
            const isOpen = expanded === u.user_id;
            const statusLabel = u.is_complete ? "Completo" : u.alert_incomplete ? "Incompleto · fuera de plazo" : "Pendiente · dentro del plazo";
            const statusColor = u.is_complete ? "#16a34a" : u.alert_incomplete ? "#BF3437" : "#d97706";
            return (
              <div key={u.user_id} className="card" data-testid={`work-checklist-user-${u.user_id}`}>
                <button onClick={() => setExpanded(isOpen ? null : u.user_id)} style={{ width: "100%", display: "flex", justifyContent: "space-between", alignItems: "center", background: "none", border: "none", cursor: "pointer", padding: 0, textAlign: "left" }}>
                  <div>
                    <div style={{ fontWeight: 800, fontSize: 13, color: "#0A0A0A" }}>{u.user_name}</div>
                    <div style={{ fontSize: 11, color: "var(--invict-text-muted)" }}>
                      {u.client_name || "Sin objetivo detectado"}{u.site_name ? ` · ${u.site_name}` : ""}
                      {u.sin_turno_cargado && <span style={{ marginLeft: 6, color: "#6B655A", fontWeight: 700 }}><CalendarClock size={10} style={{ verticalAlign: "-1px" }} /> sin turno cargado</span>}
                      {u.own_checklist && <span style={{ marginLeft: 6, color: "#0A0A0A", fontWeight: 700 }} data-testid={`work-checklist-own-badge-${u.user_id}`}>· incluye catálogo del objetivo ({u.total_items})</span>}
                    </div>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    {u.missing_items.length > 0 && (
                      <span style={{ fontSize: 11, fontWeight: 800, color: "#BF3437", display: "inline-flex", alignItems: "center", gap: 4 }}>
                        <AlertTriangle size={12} /> {u.missing_items.length} faltante{u.missing_items.length > 1 ? "s" : ""}
                      </span>
                    )}
                    <span style={{ fontSize: 11, fontWeight: 800, color: statusColor }}>{u.completed_count}/{u.total_items} · {statusLabel}</span>
                    {isOpen ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                  </div>
                </button>
                {isOpen && (
                  <div style={{ marginTop: 10, paddingTop: 10, borderTop: "1px solid var(--invict-bone-3)" }}>
                    {u.items.map((it) => (
                      <div key={it.item_id} style={{ display: "flex", gap: 8, alignItems: "flex-start", padding: "5px 0", fontSize: 12 }} data-testid={`work-checklist-item-${u.user_id}-${it.item_id}`}>
                        <span style={{ fontWeight: 800, minWidth: 130, color: it.status === "verificado" ? "#16a34a" : it.status === "faltante" ? "#BF3437" : "var(--invict-text-muted)" }}>
                          {it.status === "verificado" ? "Verificado" : it.status === "faltante" ? "Faltante" : "Sin registrar"}
                        </span>
                        <span style={{ flex: 1 }}>
                          {it.item_name}
                          {it.notes && <span style={{ color: "var(--invict-text-muted)" }}> — {it.notes}</span>}
                        </span>
                        {it.attachments?.length > 0 ? (
                          <a href={fileSrc(it.attachments[0].url)} target="_blank" rel="noreferrer" data-testid={`work-checklist-item-photo-${u.user_id}-${it.item_id}`}>
                            {it.attachments[0].kind === "video" ? <Video size={14} /> : <Camera size={14} />}
                            {it.attachments.length > 1 && <span style={{ fontSize: 9, fontWeight: 800, marginLeft: 2 }}>+{it.attachments.length - 1}</span>}
                          </a>
                        ) : it.photo_url && (
                          <a href={fileSrc(it.photo_url)} target="_blank" rel="noreferrer" data-testid={`work-checklist-item-photo-${u.user_id}-${it.item_id}`}>
                            <Camera size={14} />
                          </a>
                        )}
                      </div>
                    ))}
                    <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 10, paddingTop: 10, borderTop: "1px dashed var(--invict-bone-3)" }} data-testid={`work-checklist-closure-${u.user_id}`}>
                      {(u.closure_attachments?.length > 0 || u.closure_photo_url) ? (
                        <>
                          <AttachmentGrid items={u.closure_attachments?.length ? u.closure_attachments.slice(0, 3) : [{ url: u.closure_photo_url, kind: "image" }]} />
                          <span style={{ fontSize: 11, fontWeight: 700, color: "#16a34a" }}>Evidencia general del checklist adjunta</span>
                        </>
                      ) : u.closure_at ? (
                        <span style={{ fontSize: 11, fontWeight: 700, color: "#16a34a" }}>Cerró el checklist sin foto general</span>
                      ) : (
                        <span style={{ fontSize: 11, color: "var(--invict-text-muted)" }}>Todavía no cerró el checklist.</span>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function Counter({ label, value, color, testid }) {
  return (
    <div className="card" style={{ padding: "10px 16px", minWidth: 160 }} data-testid={testid}>
      <div style={{ fontSize: 22, fontWeight: 900, color }}>{value}</div>
      <div style={{ fontSize: 11, color: "var(--invict-text-muted)", fontWeight: 700 }}>{label}</div>
    </div>
  );
}
